import { Gene } from 'api'
import { isMobile, normalizeInput, record_url } from 'shared'
import {
    children,
    Component,
    createEffect,
    createMemo,
    createSignal,
    For,
    JSX,
    mergeProps,
    on,
    onCleanup,
    onMount,
    Show,
} from 'solid-js'

import { ArrowDownIcon } from 'icons/account'
import { PlusIcon } from 'icons/eatery'
import { CloseIcon } from 'icons/login'
import {
    addToGlobalInterval,
    getGlobalNow,
    globalCounter,
    removeGlobalInterval,
} from 'shared/globalCounter'
import { createStore } from 'solid-js/store'
import './style/helpers.scss'

type AvatarProps = {
    photo: Gene
    checksum?: string | null

    attrs?: JSX.ImgHTMLAttributes<HTMLImageElement>
}
export const Avatar: Component<AvatarProps> = P => {
    const url = createMemo(() => {
        if (!P.photo) return '/public/imgs/account/avatar.webp'
        let ch = P.checksum ? '?checksum=' + P.checksum : ''
        return record_url(P.photo) + ch
    })

    return (
        <img
            src={url()}
            draggable={false}
            loading='lazy'
            decoding='async'
            {...P.attrs}
        />
    )
}

interface WorldCupBannerProps {
    onClick(): void
}
export const WorldCupBanner: Component<WorldCupBannerProps> = P => {
    return (
        <button type='button' class='world-cup-banner-cmp' onclick={P.onClick}>
            <div class='background-img'></div>

            <div class='texts'>
                <div class='main'>
                    از <span>جام جهانی</span> جا نمونی!
                </div>
                <div class='sub'>
                    ببین کجاها نزدیکت جام جهانی رو پخش می‌کنه!
                </div>
            </div>
        </button>
    )
}
interface WorldCupBannerProps {
    onClick(): void
}
export const HbdBanner: Component<WorldCupBannerProps> = P => {
    return (
        <button type='button' class='hbd-banner-cmp' onclick={P.onClick}>
            <div class='background-img'></div>

            <div class='texts'>
                <div class='main'>امسال تولدتو خفن بگیر!</div>
                <div class='sub'>بیا بهت بگم کجاها میتونی تولد بگیری!</div>
            </div>
        </button>
    )
}

interface BannerDefaultProps {
    onClick(): void
    class?: string
    title: string
    subtitle: string
    svg: string
    instant?: boolean
}

export const GoojeBanner: Component<BannerDefaultProps> = P => {
    return (
        <div class='gooje-banners-cmp' classList={{ [P.class || '']: true }}>
            <button
                class='event-wrapper'
                type='button'
                onclick={() => {
                    P.onClick()
                }}
            >
                <div class='img-wrapper'>
                    <img
                        draggable={false}
                        src={P.svg}
                        loading={P.instant ? 'eager' : 'lazy'}
                        decoding='async'
                    />
                </div>
                <div class='texts '>
                    <h3 class='banner-head section_title'>{P.title}</h3>
                    <p class='sub-head title_small'>{P.subtitle}</p>
                </div>
            </button>
        </div>
    )
}
interface CounterProps {
    shouldStart?: boolean
    end: number
    /** in milliseconds */
    duration?: number
    format?: boolean
    start?: number
    onEnd?(): void
    iterations?: number
    class?: string
    steps?: number
    interval?: number
    startDelay?: number
}
export const Counter: Component<CounterProps> = props => {
    let timer: ReturnType<typeof setTimeout> | null = null
    let delay: ReturnType<typeof setInterval> | null = null

    const [state, setState] = createStore<{ counter: number }>({
        counter: props.start || 0,
    })

    createEffect(() => {
        const shouldStart = props.shouldStart ?? true

        if (!shouldStart) {
            if (timer !== null) {
                clearInterval(timer)
                timer = null
            }
            return
        }

        if (timer !== null) {
            clearInterval(timer)
            timer = null
        }

        const interval = props.interval ?? 20
        const totalDuration = Math.max(props.duration || 1000, interval)
        const iterations = props.iterations ?? totalDuration / interval
        const steps =
            props.steps ?? Math.max(Math.ceil(props.end / iterations), 1)

        delay = setTimeout(() => {
            timer = setInterval(() => {
                setState('counter', current => {
                    const nextValue = Math.floor((current + steps) * 10) / 10
                    if (nextValue >= props.end) {
                        if (timer !== null) {
                            clearInterval(timer)
                            timer = null
                        }
                        props.onEnd?.()
                        return props.end
                    }
                    return nextValue
                })
            }, interval)
        }, props.startDelay ?? 0)
    })

    onCleanup(() => {
        if (timer !== null) clearInterval(timer)
        if (delay !== null) clearTimeout(delay)
    })

    return (
        <span class='counter-span' classList={{ [props.class || '']: true }}>
            <Show when={props.format} fallback={state.counter}>
                {state.counter.toLocaleString()}
            </Show>
        </span>
    )
}

type itemType = {
    name: string
    value: string
    Icon?: JSX.Element
    priority?: number
}
export { type itemType as DropdownItem }
interface dropdownProps {
    class?: string
    onchange: (value: string) => void
    active: string
    items: itemType[]

    blur?: boolean
    maxHeight?: number
    disable?: boolean
}
export const Dropdown: Component<dropdownProps> = P => {
    const [active, setActive] = createSignal(false)

    let ref: HTMLElement
    let itemsRef: HTMLDivElement

    const closeOnOutsideClick = (e: MouseEvent) => {
        if (ref && !ref.contains(e.target as Node)) {
            close()
        }
    }

    onMount(() => {
        if (P.blur) {
            ref.addEventListener('blur', close)
            onCleanup(() => {
                ref.removeEventListener('blur', close)
            })
        }

        document.addEventListener('click', closeOnOutsideClick, {
            passive: true,
        })

        onCleanup(() => {
            document.removeEventListener('click', closeOnOutsideClick)
        })
    })

    createEffect(
        on(
            () => P.disable,
            disable => {
                if (!disable) return

                close()
            }
        )
    )

    function close() {
        setActive(false)

        if (itemsRef) itemsRef.style.maxHeight = `0`
    }

    const toggle = () => {
        if (P.disable) return

        setActive(s => {
            let now = !s

            if (itemsRef) {
                if (now) {
                    itemsRef.style.maxHeight = `${P.maxHeight || itemsRef.scrollHeight}px`
                } else {
                    itemsRef.style.maxHeight = `0`
                }
            }

            return now
        })
    }

    return (
        <div
            class={`dropdown-cmp-container ${P.class || ''}`}
            classList={{ disable: P.disable, active: active() }}
            ref={e => (ref = e)}
        >
            <button
                type='button'
                class='active-wrapper title_smaller'
                onclick={toggle}
            >
                <span>{P.active}</span>
                <ArrowDownIcon />
            </button>
            <div class='items-wrapper' ref={e => (itemsRef = e)}>
                <Show when={P.items}>
                    <For each={P.items}>
                        {item => (
                            <button
                                class='item title_smaller'
                                onclick={() => {
                                    P.onchange(item.value)
                                    close()
                                }}
                            >
                                {item.name}
                            </button>
                        )}
                    </For>
                </Show>
            </div>
        </div>
    )
}

type SearchableDropdownProps = {
    class?: string
    onchange: (value: string) => void
    active: string
    items: itemType[]

    blur?: boolean

    placeholder?: string

    disabled?: boolean
}
export const SearchableDropdown: Component<SearchableDropdownProps> = P => {
    let itemsRef: HTMLElement

    type STATE = {
        open: boolean
        val: string
        query: string
    }
    const [state, setState] = createStore<STATE>({
        open: false,
        val: '',
        query: '',
    })

    let ref!: HTMLElement
    let inputRef!: HTMLInputElement

    createEffect(
        on(
            () => {
                P.active
                P.disabled
            },
            () => {
                reset()
            }
        )
    )

    const reset = () => {
        setState({
            val: P.active,
            open: false,
            query: '',
        })
        inputRef?.blur()
        itemsRef?.scrollTo({
            top: 0,
            behavior: 'instant',
        })
    }

    onMount(() => {
        if (!P.blur) return

        document.addEventListener('click', closeOnOutsideClick)

        onCleanup(() => {
            document.removeEventListener('click', closeOnOutsideClick)
        })
    })

    // filtered items based on query (case-insensitive match on name)
    const filteredItems = createMemo(() => {
        const q = normalizeInput(state.query.trim().toLocaleLowerCase())

        const items = !q
            ? [...P.items]
            : P.items.filter(item => item.name.toLocaleLowerCase().includes(q))

        if (!q) return items

        return items.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
    })

    const closeOnOutsideClick = (e: MouseEvent) => {
        if (ref && !ref.contains(e.target as Node)) {
            reset()
        }
    }

    function selectItem(item: itemType) {
        P.onchange(item.value)
        reset()
    }

    function onKeyDown(e: KeyboardEvent) {
        if (e.key === 'Escape') {
            reset()
        } else if (e.key === 'Enter') {
            const items = filteredItems()
            if (items.length === 1 && items[0]) {
                selectItem(items[0])
            } else {
                let q = state.query

                const exact = items.find(
                    it =>
                        it.name.toLowerCase() === q.trim().toLowerCase() ||
                        it.value.toLowerCase() === q.trim().toLowerCase()
                )

                if (exact) selectItem(exact)
            }
        }
    }

    return (
        <div
            class={`dropdown-cmp-container searchable ${P.class || ''}`}
            classList={{ active: state.open, disable: P.disabled }}
            ref={e => (ref = e)}
        >
            <div
                role='button'
                class='active-wrapper title_smaller'
                onclick={() => {
                    setState({ open: !state.open })

                    if (state.open) {
                        const len = inputRef.value.length
                        inputRef?.focus()
                        inputRef.setSelectionRange(len, len)
                    } else {
                        reset()
                    }
                }}
            >
                <input
                    ref={el => (inputRef = el)}
                    type='text'
                    class='dropdown-input title_smaller'
                    value={state.val ? state.val : state.query}
                    placeholder={
                        P.placeholder ? P.placeholder : 'انتخاب کنید...'
                    }
                    onclick={e => {
                        if (!state.open) return
                        if (state.query.length == 0) return

                        e.stopPropagation()
                    }}
                    oninput={e => {
                        const v = normalizeInput(
                            (e.target as HTMLInputElement).value
                        )

                        setState({
                            query: v,
                            val: '',
                        })
                    }}
                    onkeydown={onKeyDown}
                    autocomplete='off'
                />
                <div class='icon'>
                    <ArrowDownIcon />
                </div>
            </div>

            <div class='items-wrapper' ref={e => (itemsRef = e)}>
                <Show when={P.items && P.items.length > 0}>
                    {filteredItems().length > 0 ? (
                        filteredItems().map(item => (
                            <button
                                type='button'
                                class='item title_smaller'
                                onclick={() => selectItem(item)}
                            >
                                {item.Icon}
                                {item.name}
                            </button>
                        ))
                    ) : (
                        <div class='dropdown-no-results title_smaller'>
                            <CloseIcon />
                            نتیجه ای یافت نشد!
                        </div>
                    )}
                </Show>
            </div>
        </div>
    )
}

interface DynamicImgProps {
    src: string
}
export const DynamicImg: Component<DynamicImgProps> = P => {
    let imgRef: HTMLImageElement | undefined
    const [objectFit, setObjectFit] = createSignal<'cover' | 'contain'>(
        'contain'
    )

    const applyFit = () => {
        if (!imgRef) return
        const w = imgRef.naturalWidth || 0
        const h = imgRef.naturalHeight || 0

        // if we can't determine sizes, keep contain as safe default
        if (!w || !h) {
            setObjectFit('contain')
            return
        }

        // portrait -> cover, otherwise contain (square or landscape)
        if (h > w) setObjectFit('contain')
        else setObjectFit('cover')
    }

    return (
        <img
            onload={applyFit}
            src={P.src}
            ref={e => (imgRef = e)}
            style={{ 'object-fit': objectFit() }}
            draggable={false}
            loading='lazy'
            decoding='async'
        />
    )
}

interface FancyBtnProps {
    onclick(): void
    title: string
    class?: string
    animate: boolean

    icon: () => JSX.Element | null

    forwardRef?: (el: HTMLButtonElement) => void
}

export const FancyBtn: Component<FancyBtnProps> = P => {
    let i = P.icon()

    let btnRef: HTMLButtonElement

    onMount(() => {
        btnRef && P.forwardRef?.(btnRef)
    })

    return (
        <button
            ref={e => (btnRef = e)}
            class={`fancy-btn-cmp ${P.animate ? 'animate' : ''} ${P.class || ''}`}
            onclick={P.onclick}
            type='button'
        >
            <div class='btn-wrapper'>
                <Show when={typeof i === 'string'} fallback={i}>
                    <img src={i as string} alt='' />
                </Show>

                <span>{P.title} </span>
            </div>
            <span class='dot dot-1'></span>
            <span class='dot dot-2'></span>
            <span class='dot dot-3'></span>
            <span class='dot dot-4'></span>
            <span class='dot dot-5'></span>
            <span class='dot dot-6'></span>
            <span class='dot dot-7'></span>
        </button>
    )
}

interface LogoProps {
    size?: number
    classList?: string
}

export const Logo: Component<LogoProps> = P => {
    return (
        <div class={`logo ${P.classList || ''}`}>
            <div class='svg-wrapper'>
                <svg
                    width={P.size || 100}
                    height={P.size || 100}
                    viewBox='0 0 126.99999 127.00005'
                    version='1.1'
                    id='svg1'
                    xmlns='http://www.w3.org/2000/svg'
                >
                    <g
                        id='layer1-9'
                        transform='matrix(0.45145861,0,0,0.45145861,16.086023,-4.0643947)'
                        style='stroke-width:1.65301'
                    >
                        <path
                            style='fill:#e13a34;stroke:none;stroke-width:1.65301'
                            d='m 63.369062,117.39147 c -5.651496,0.7442 -10.992885,1.87798 -16.139583,4.45134 -31.066272,15.53314 -35.028353,57.15849 -20.910255,85.39469 16.128118,32.25624 52.221974,45.73267 86.526926,41.46426 20.56502,-2.55882 41.21741,-11.41228 55.78215,-26.40668 23.2584,-23.94451 34.23451,-67.39034 6.39493,-92.53557 -10.13023,-9.1498 -23.51849,-13.3829 -37.04167,-12.45667 -11.59118,0.79391 -22.69688,4.49236 -34.39583,4.20578 -13.323364,-0.32636 -26.845181,-5.87796 -40.216668,-4.11715 M 185.0774,171.78333 h -0.26459 c -6.76922,-20.00809 -24.72136,-36.83806 -43.92083,-44.71458 1.43276,-0.60122 3.26427,-0.20528 4.7625,0 4.07947,0.55898 8.10793,1.51764 11.90625,3.1354 16.14982,6.87843 27.46764,23.81124 27.51667,41.57918 M 41.408646,135.53542 c -0.94886,3.31914 -2.557257,6.48489 -3.61312,9.78958 -2.101809,6.57835 -3.621613,13.49317 -4.27947,20.37292 -1.087466,11.37262 -0.522401,22.93051 1.862919,34.13125 1.225539,5.75475 3.388084,11.23355 4.706754,16.93333 h -0.264583 c -3.592853,-5.45432 -7.162903,-10.56079 -9.629726,-16.66875 -6.268658,-15.52147 -4.959145,-32.34027 1.267994,-47.625 2.195377,-5.38862 5.347365,-13.18674 9.949232,-16.93333 z'
                            id='path32'
                        />
                        <g
                            id='g32'
                            transform='translate(0.05270814,0.01756976)'
                            style='stroke-width:1.65301'
                        >
                            <path
                                style='opacity:1;fill:#12e647;fill-opacity:1;stroke:#7caa99;stroke-width:0;stroke-dasharray:none;paint-order:markers fill stroke'
                                d='m 118.6553,100.3415 -2.3514,0.36176 -2.98447,0.31653 -3.52711,-0.58785 -2.71315,-0.949603 -2.75837,-1.673111 -1.71833,-1.673114 1.22092,-0.542628 1.85398,-0.768729 1.71833,-0.949603 1.80878,-1.085262 2.30618,-1.808771 2.21574,-1.899208 1.89921,-2.034866 1.76355,-2.125305 0.90438,-1.13048 1.4018,-2.260963 1.4018,-2.170522 1.67311,-3.391445 0.90438,-1.989646 0.94961,-2.396619 0.99482,-2.713157 0.76873,-2.713154 0.76873,-2.622716 0.36175,-2.080086 0.18088,-0.994822 0.54263,-0.768729 0.90438,-0.723505 1.1757,-0.226098 0.99483,0.09044 1.08526,0.723508 0.58785,0.723509 0.27131,0.633068 0.18088,0.633071 c 0,0 0.13566,0.587849 0.0452,0.904383 -0.0904,0.316537 -0.31654,1.35658 -0.31654,1.35658 l -0.36175,1.401797 -0.40697,0.859165 -0.40698,1.220917 c 0,0 -0.45219,0.678291 -0.54263,1.130483 -0.0904,0.452191 -0.63307,2.034865 -0.63307,2.034865 l -0.81394,2.39662 -1.80877,4.612365 -1.44702,3.934073 -1.89921,4.702802 -2.39662,5.697625 -2.80359,6.149816 z'
                                id='path30'
                            />
                            <path
                                style='opacity:1;fill:#12e647;fill-opacity:1;stroke:#7caa99;stroke-width:0;stroke-dasharray:none;paint-order:markers fill stroke'
                                d='m 64.077569,92.727019 c 0,0 3.453281,10.999341 14.132874,18.097761 10.679597,7.09841 23.725327,6.90656 23.661377,6.90656 -0.064,0 8.76111,0.76739 23.59743,-5.05202 14.83632,-5.81942 25.64382,-25.196173 25.64382,-25.196173 0,0 6.39496,-10.423797 11.76674,-25.451969 5.37177,-15.028172 3.06958,-13.36548 4.02882,-14.516573 0.95925,-1.151097 -11.76673,30.631891 -29.54474,46.235608 -17.77801,15.603717 -33.12593,7.418167 -33.12593,7.418167 0,0 -16.603064,-6.388845 -18.864026,-37.680562 l -0.271317,-0.384363 -1.198309,5.720236 -0.203488,1.853988 -0.384363,2.667934 -0.339143,5.403702 0.203486,5.878502 1.605283,7.099421 c 0,0 4.999251,12.574672 12.86506,16.795352 C 79.659803,100.38198 58.647522,90.794291 64.077569,92.727019 Z'
                                id='path31'
                            />
                        </g>
                    </g>
                </svg>
            </div>
        </div>
    )
}

let IMGS = {
    small: '/public/svgs/mascot/idk.svg',
    big: '/public/svgs/mascot/idk_big.svg',
}

interface NotFoundProps {
    title?: string
}

const MIN_WIDTH = 100
interface NotFoundProps {
    show?: 'small' | 'big'
    class?: string
}
export const NotFound: Component<NotFoundProps> = P => {
    const [parentSize, setSize] = createSignal<number>(0)

    let ref: HTMLElement

    createEffect(() => {
        if (!ref || P.show) return

        let parent = ref.parentNode as HTMLElement

        let p_width = parent?.clientWidth || 0

        const updateSize = () => {
            setSize(p_width)
        }

        // Initial size check
        updateSize()

        window.addEventListener('resize', updateSize)

        onCleanup(() => {
            window.removeEventListener('resize', updateSize)
        })
    })

    if (!P.show) {
        P.show = parentSize() < MIN_WIDTH ? 'small' : 'big'
    }

    return (
        <div
            class={`not-found-comp ${P.show || ''}`}
            classList={{ [P.class || '']: true }}
            ref={e => (ref = e)}
        >
            <img
                draggable={false}
                src={IMGS[P.show]}
                loading='lazy'
                decoding='async'
            />
            {P.title}
        </div>
    )
}

interface LoadingElemProps {
    class?: string
}

export const LoadingElem: Component<LoadingElemProps> = P => {
    return <div class={`loading-elem-cmp ${P.class || ''}`}></div>
}

type MarqueeProps = {
    style?: JSX.CSSProperties
    className?: string
    play?: boolean
    pauseOnHover?: boolean
    pauseOnClick?: boolean
    direction?: 'left' | 'right'
    speed?: number
    delay?: number
    iterations?: number
    onFinish?: () => void
    onCycleComplete?: () => void
    children: JSX.Element
}
const MarqueedefaultProps = {
    style: {},
    className: '',
    play: true,
    pauseOnHover: true,
    pauseOnClick: false,
    direction: 'left',
    speed: 20,
    delay: 0,
    iterations: 0,
    onFinish: () => {},
    onCycleComplete: () => {},
    children: <></>,
} satisfies MarqueeProps
export const Marquee: Component<MarqueeProps> = incoming => {
    const P = mergeProps(MarqueedefaultProps, incoming)

    let containerRef!: HTMLDivElement
    let marqueeRef!: HTMLDivElement

    const [containerWidth, setContainerWidth] = createSignal(0)
    const [marqueeWidth, setMarqueeWidth] = createSignal(0)
    const [duration, setDuration] = createSignal(0)

    onMount(() => {
        calculateWidths()

        const handleResize = () => calculateWidths()
        window.addEventListener('resize', handleResize, { passive: true })

        onCleanup(() => {
            window.removeEventListener('resize', handleResize)
        })
    })

    const calculateWidths = () => {
        if (containerRef && marqueeRef) {
            setContainerWidth(containerRef.getBoundingClientRect().width)
            setMarqueeWidth(marqueeRef.getBoundingClientRect().width)
        }
    }

    createEffect(() => {
        if (marqueeWidth() < containerWidth()) {
            setDuration(containerWidth() / P.speed)
        } else {
            setDuration(marqueeWidth() / P.speed)
        }
    })

    const resolved1 = children(() => P.children)
    const resolved2 = children(() => P.children)
    const resolved3 = children(() => P.children)

    return (
        <div
            ref={containerRef}
            class={`marquee-root-cmp ${P.className}`}
            style={{
                ...P.style,
                ['--pause-on-hover']:
                    !P.play || P.pauseOnHover ? 'paused' : 'running',
                ['--pause-on-click']:
                    !P.play ||
                    P.pauseOnClick ||
                    (!P.pauseOnClick && P.pauseOnHover)
                        ? 'paused'
                        : 'running',
            }}
            onAnimationIteration={P.onCycleComplete}
            onAnimationEnd={P.onFinish}
        >
            <div
                ref={marqueeRef}
                class='marquee'
                style={{
                    ['--play']: P.play ? 'running' : 'paused',
                    ['--direction']:
                        P.direction == 'left' ? 'normal' : 'reverse',
                    ['--duration']: `${duration()}s`,
                    ['--delay']: `${P.delay}s`,
                    ['--iterations']: !!P.iterations
                        ? `${P.iterations}`
                        : 'infinite',
                }}
            >
                {resolved1()}
            </div>
            <div
                class='marquee'
                aria-hidden={true}
                style={{
                    ['--play']: P.play ? 'running' : 'paused',
                    ['--direction']:
                        P.direction == 'left' ? 'normal' : 'reverse',
                    ['--duration']: `${duration()}s`,
                    ['--delay']: `${P.delay}s`,
                    ['--iterations']: !!P.iterations
                        ? `${P.iterations}`
                        : 'infinite',
                }}
            >
                {resolved2()}
            </div>
            <div
                class='marquee'
                aria-hidden={true}
                style={{
                    ['--play']: P.play ? 'running' : 'paused',
                    ['--direction']:
                        P.direction == 'left' ? 'normal' : 'reverse',
                    ['--duration']: `${duration()}s`,
                    ['--delay']: `${P.delay}s`,
                    ['--iterations']: !!P.iterations
                        ? `${P.iterations}`
                        : 'infinite',
                }}
            >
                {resolved3()}
            </div>
        </div>
    )
}

type StickyBtnT = {
    onclick?(): void
    class?: string
    label: string
    Icon?: JSX.Element
    type?: 'submit' | 'reset' | 'button' | 'menu' | undefined
    disabled?: boolean
}
export const StickyBtn: Component<StickyBtnT> = P => {
    let btnRef: HTMLElement

    let targetX = 0,
        targetY = 0
    let currentX = 0,
        currentY = 0
    let animFrame = 0

    onMount(() => {
        const ac = new AbortController()

        btnRef.addEventListener('mousemove', mouseMove, { signal: ac.signal })
        btnRef.addEventListener('mouseenter', mouseEnter, { signal: ac.signal })
        btnRef.addEventListener('mouseleave', mouseLeave, { signal: ac.signal })

        onCleanup(() => {
            ac.abort()
            // cancel RAF on unmount to avoid leaks
            if (animFrame) cancelAnimationFrame(animFrame)
            animFrame = 0
        })
    })

    function animate() {
        // ease current→target
        currentX += (targetX - currentX) * 0.05
        currentY += (targetY - currentY) * 0.05

        // normalized coords in range [-1,1]
        const nx = currentX / (btnRef.clientWidth / 2)
        const ny = currentY / (btnRef.clientHeight / 2)

        // twist amount
        const twist = Math.max(-1, Math.min(1, nx * ny)) * 100

        btnRef.style.transform = `
      translate(${currentX}px, ${currentY}px)
      rotateX(${ny * -10}deg)
      rotateY(${nx * 10}deg)
      rotateZ(${twist * 5}deg)
    `

        // continue the loop until we're close enough to target
        if (
            Math.abs(targetX - currentX) > 0.5 ||
            Math.abs(targetY - currentY) > 0.5
        ) {
            animFrame = requestAnimationFrame(animate)
        } else {
            // stop firing RAF when target reached
            animFrame = 0
        }
    }

    const mouseMove = (e: MouseEvent) => {
        const pos = btnRef.getBoundingClientRect()
        // compute target offsets
        targetX = (e.clientX - pos.left - pos.width / 2) * 0.25
        targetY = (e.clientY - pos.top - pos.height / 2) * 0.1

        // start the animation loop if not running
        if (!animFrame) animate()
    }

    const mouseEnter = () => {
        btnRef.style.transition = 'background 0.2s ease, color 0.2s ease'
        if (!animFrame) animate()
    }

    const mouseLeave = () => {
        targetX = 0
        targetY = 0

        btnRef.style.transition = 'background 0.2s ease, color 0.2s ease'

        if (!animFrame) animate()
    }

    return (
        <button
            onclick={P.onclick}
            class={`sticky-btn-cmp   ${P.class || ''}`}
            ref={e => (btnRef = e)}
            type={P.type || 'button'}
            disabled={P.disabled}
        >
            {P.label}
            {P.Icon}
        </button>
    )
}

type TimerProps = {
    seconds: number
    class?: string
}
export const Timer: Component<TimerProps> = P => {
    function convertSectoMin(seconds: number) {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return mins + ':' + (secs < 10 ? '0' : '') + secs
    }

    return (
        <span class={`timer-wrapper ${P.class}`}>
            {convertSectoMin(P.seconds)}
        </span>
    )
}

type IsProps = {
    children: JSX.Element
    not?: true
    fallback?: JSX.Element
}

export const IsClient: Component<IsProps> = P => {
    const [is_client, setIsClient] = createSignal(false)

    onMount(() => {
        setIsClient(true)
    })

    return (
        <Show when={P.not ? !is_client() : is_client()} fallback={P.fallback}>
            {P.children}
        </Show>
    )
}

export const IsMobile: Component<IsProps> = P => {
    const [is_mobile, setIsMobile] = createSignal(false)

    onMount(() => {
        setIsMobile(isMobile())
    })

    return (
        <Show when={P.not ? !is_mobile() : is_mobile()} fallback={P.fallback}>
            {P.children}
        </Show>
    )
}

export const LoadingDots: Component<{}> = () => {
    return (
        <div class='loading-dots-cmp'>
            <span class='dot'></span>
            <span class='dot'></span>
            <span class='dot'></span>
        </div>
    )
}

export const LoadingSpin: Component = () => {
    return <div class='loading-spinner-cmp'></div>
}

interface LoaderImgPrpos {
    photo: string
    onclick?: () => void
    class?: string
    alt?: string
}
export const LoaderImg: Component<LoaderImgPrpos> = P => {
    const [loading, setLoading] = createSignal(false)
    onMount(() => setLoading(true))

    return (
        <div
            class={`loader-img-cmp ${P.class || ''}`}
            role={P.onclick ? 'button' : undefined}
            onclick={e => {
                if (P.onclick) {
                    e.preventDefault()
                    e.stopPropagation()
                    P.onclick()
                }
            }}
        >
            <Show when={loading()}>
                <LoadingElem />
            </Show>
            <img
                draggable='false'
                src={P.photo}
                loading='lazy'
                decoding='async'
                onload={() => setLoading(false)}
                alt={P.alt}
            />
        </div>
    )
}

interface AddCtaProps {
    onClick(): void
    class?: string
    label: string
    holdActive?: boolean
}
export const AddCta: Component<AddCtaProps> = P => {
    return (
        <button
            class={`add-cta-cmp title_small ${P.class || ''}`}
            onclick={P.onClick}
            classList={{ active: P.holdActive }}
            type='button'
        >
            <PlusIcon />
            <span>{P.label}</span>
            <PlusIcon />
        </button>
    )
}

interface ObserverProps {
    children: (P: { observed: boolean }) => JSX.Element
    alreadyActive?: boolean
    observerOptions?: IntersectionObserverInit
    createObserver?: boolean
}
export function ObserverWrapper(P: ObserverProps) {
    const [observed, setObserved] = createSignal(!!P.alreadyActive)

    let el: HTMLElement

    createEffect(() => {
        if (P.createObserver === false || P.alreadyActive) {
            setObserved(!!P.alreadyActive)
            return
        }

        if (!el) return

        // New observer = new observation cycle
        setObserved(false)

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry?.isIntersecting) {
                    setObserved(true)
                    observer.disconnect()
                }
            },
            P.observerOptions || { threshold: 0.1 }
        )

        observer.observe(el)

        onCleanup(() => observer.disconnect())
    })

    return (
        <div ref={e => (el = e)} class='observer-helper-cmp'>
            {P.children({ observed: observed() })}
        </div>
    )
}

export const WarningIfLandscape: Component = () => {
    return (
        <div class='warning-if-landscape-cmp title_small'>
            <img src='/public/svgs/mascot/phone.svg' />

            <p>گوجه فقط رو حالت عمودی کار می‌کنه</p>
        </div>
    )
}

interface CountdownProps {
    timestamp: number
    onComplete?: () => void

    class?: string
    itemClass?: string
    valueClass?: string
    labelClass?: string

    hoursLabel?: string
    minutesLabel?: string
    secondsLabel?: string
}
export default function Countdown(P: CountdownProps) {
    type CountdownParts = {
        hours: number
        minutes: number
        seconds: number
    }

    let hoursRef: HTMLDivElement | undefined
    let minutesRef: HTMLDivElement | undefined
    let secondsRef: HTMLDivElement | undefined

    const requestId = performance.now()
    let completed = false

    const write = (parts: CountdownParts) => {
        if (hoursRef) hoursRef.innerText = pad2(parts.hours)
        if (minutesRef) minutesRef.innerText = pad2(parts.minutes)
        if (secondsRef) secondsRef.innerText = pad2(parts.seconds)
    }

    const splitTime = (msLeft: number): CountdownParts => {
        const totalSeconds = Math.max(0, Math.floor(msLeft / 1000))
        const hours = Math.floor(totalSeconds / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60
        return { hours, minutes, seconds }
    }

    const sync = () => {
        const now = getGlobalNow()
        const remaining = P.timestamp - now

        if (remaining <= 0) {
            write({ hours: 0, minutes: 0, seconds: 0 })

            if (!completed) {
                completed = true
                P.onComplete?.()
            }

            return
        }

        completed = false
        write(splitTime(remaining))
    }

    onMount(() => {
        addToGlobalInterval(requestId)
    })
    onCleanup(() => {
        removeGlobalInterval(requestId)
    })

    createEffect(
        on(
            () => [P.timestamp, globalCounter()],
            () => {
                sync()
            }
        )
    )

    function pad2(value: number): string {
        return String(value).padStart(2, '0')
    }

    return (
        <div class={`countdown-cmp ${P.class ?? ''}`}>
            <div class={`countdown-group ${P.itemClass ?? ''}`}>
                <div
                    ref={hoursRef}
                    class={`countdown-data title ${P.valueClass ?? ''}`}
                >
                    00
                </div>
                <div
                    class={`countdown-holder description ${P.labelClass ?? ''}`}
                >
                    {P.hoursLabel ?? 'ساعت'}
                </div>
            </div>

            <div class={`countdown-group ${P.itemClass ?? ''}`}>
                <div
                    ref={minutesRef}
                    class={`countdown-data title ${P.valueClass ?? ''}`}
                >
                    00
                </div>
                <div
                    class={`countdown-holder description ${P.labelClass ?? ''}`}
                >
                    {P.minutesLabel ?? 'دقیقه'}
                </div>
            </div>

            <div class={`countdown-group ${P.itemClass ?? ''}`}>
                <div
                    ref={secondsRef}
                    class={`countdown-data title ${P.valueClass ?? ''}`}
                >
                    00
                </div>
                <div
                    class={`countdown-holder description ${P.labelClass ?? ''}`}
                >
                    {P.secondsLabel ?? 'ثانیه'}
                </div>
            </div>
        </div>
    )
}

export type ProgressColorStop = {
    at: number
    color: string
}
export type ProgressRingProps = {
    value: number

    size?: number

    strokeWidth?: number
    trackColor?: string
    color?: string
    colorStops?: readonly ProgressColorStop[]
    animated?: boolean
    class?: string
    ariaLabel?: string
}
const DEFAULT_COLOR_STOPS: readonly ProgressColorStop[] = [
    { at: 75, color: 'var(--alert-success)' },
    { at: 50, color: 'var(--popup-warning-color)' },
    { at: 30, color: 'var(--alert-error)' },
]
const DEFAULT_SIZE = 25
const DEFAULT_STROKE_WIDTH = 2
const DEFAULT_TRACK_COLOR = 'var(--thirty-percent)'
export const ProgressRing: Component<ProgressRingProps> = props => {
    const progress = () => {
        const value = Number(props.value)

        if (!Number.isFinite(value)) return 0

        return Math.max(0, Math.min(100, value))
    }

    const size = () => {
        const value = props.size ?? DEFAULT_SIZE

        return Number.isFinite(value) ? Math.max(1, value) : DEFAULT_SIZE
    }

    const strokeWidth = () => {
        const value = props.strokeWidth ?? DEFAULT_STROKE_WIDTH

        return Number.isFinite(value)
            ? Math.max(0.1, Math.min(value, size() - 0.1))
            : DEFAULT_STROKE_WIDTH
    }

    const center = () => size() / 2

    const radius = () => (size() - strokeWidth()) / 2

    const progressColor = () => {
        // Explicit color takes priority over automatic thresholds.
        if (props.color) return props.color

        const stops = [...(props.colorStops ?? DEFAULT_COLOR_STOPS)].sort(
            (a, b) => b.at - a.at
        )

        return (
            stops.find(stop => progress() >= stop.at)?.color ??
            stops.at(-1)?.color ??
            'var(--alert-error)'
        )
    }

    const dashOffset = () => 100 - progress()

    return (
        <svg
            class={`progress-ring-cmp ${props.class ?? ''}`.trim()}
            classList={{
                'no-animation': props.animated === false,
            }}
            viewBox={`0 0 ${size()} ${size()}`}
            width={size()}
            height={size()}
            role='progressbar'
            aria-label={props.ariaLabel ?? 'Progress'}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress()}
        >
            <circle
                class='progress-ring-track'
                cx={center()}
                cy={center()}
                r={radius()}
                stroke={props.trackColor ?? DEFAULT_TRACK_COLOR}
                stroke-width={strokeWidth()}
            />
            <circle
                class='progress-ring-value'
                cx={center()}
                cy={center()}
                r={radius()}
                stroke={progressColor()}
                stroke-width={strokeWidth()}
                pathLength='100'
                stroke-dasharray='100 100'
                stroke-dashoffset={dashOffset()}
                transform={`rotate(-90 ${center()} ${center()})`}
            />
        </svg>
    )
}

interface MatchScoreCmpProps {
    percentage: number
}
export const MatchScoreCmp: Component<MatchScoreCmpProps> = P => {
    let text = 'به سلیقت میخوره!'

    if (P.percentage <= 50) {
        text = 'خیلی به سلیقت نمیخوره'
    } else if (P.percentage <= 75) {
        text = 'تقریبا به سلیقت میخوره'
    } else if (P.percentage == 100) {
        text = 'اینجا خوراک خودته'
    }

    return (
        <div class='match-score-cmp'>
            <div class='match-score-wrapper title_small'>
                <ProgressRing strokeWidth={3} size={40} value={P.percentage} />
                <div class='percentage'>
                    <span>{P.percentage}%</span>
                </div>
            </div>
            <div class='holder title_smaller'>{text}</div>
        </div>
    )
}
